<script lang="ts">
    import axios from 'axios';
    import Shgrid from '../../components/shgrid/shgrid.svelte';
    import type {Column} from '@/components/shgrid';
    import type {CulmexJobs} from "@/types/types";
    import {SvelteMap} from "svelte/reactivity";
    import type { Snippet } from 'svelte';
    import { PicklistBadge } from '@/components/ui/picklist-badge';
    import * as Table from '@/components/ui/table/index.js';

    type Row = CulmexJobs;

    type StatusOption = {
        value: string;
        label: string;
    };

    type Props = {
        view : string;
        onRowClick?: (row: Row) => void;
        relatedData?: Record<string, any>;
        selectedJobs?: SvelteMap<string, string> | undefined;
        onSubmitClick?: () => void;
        title?: string | null;
        headerSnippet?: Snippet;
        statusOptions?: StatusOption[];
    };

    let {view, onRowClick, relatedData, selectedJobs = $bindable(), onSubmitClick, title = null, headerSnippet, statusOptions = []}: Props = $props();
    const getRowLink = (row: Row) => route('jobs.show', {job: row.id});
    const includeLink = view != 'related';
    let cols = $state<Column<Row>[]>([
        {
            label: 'Ref',
            key: 'reference',
            cellRender: {
                type: 'text',
                label: (row) => row.reference ?? '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Title',
            key: 'title',
            cellRender: {
                type: 'text',
                label: (row) => row.title,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Status',
            key: 'status',
            cellRender: {
                type: 'custom',
                snippet: statusSnippet,
                label: (row) => row.status_label ?? row.status,
            },
            sortable: true,
            filter: statusOptions.length > 0
                ? { type: 'select', options: statusOptions }
                : { type: 'string' },
        },
        {
            label: 'Postcode',
            key: 'postcode',
            cellRender: {
                type: 'text',
                label: (row) => row.postcode,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string'
            }
        },
        {
            label: 'Start Date',
            key: 'start_date_time',
            cellRender: {
                type: 'date',
                label: (row) => row.start_date_time,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        },
        {
            label: 'End Date',
            key: 'end_date_time',
            cellRender: {
                type: 'date',
                label: (row) => row.end_date_time,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        },
        {
            label: 'Primary Customer',
            key: 'primary_customer',
            cellRender: {
                type: 'text',
                label: (row) => row.primary_customer || '',
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Customers',
            key: 'customers',
            cellRender: {
                type: 'text',
                label: (row) => row.customers.map((customer) => customer.name).join(", "),
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'string',
            },
        },
        {
            label: 'Created At',
            key: 'created_at',
            cellRender: {
                type: 'date',
                label: (row) => row.created_at,
                ...(includeLink && { link: getRowLink }),
            },
            sortable: true,
            filter: {
                type: 'date',
            },
        }
    ]);
</script>

{#snippet statusSnippet(row: Row)}
    <Table.Cell>
        {#if row.status_label && row.status_color}
            <PicklistBadge label={row.status_label} color={row.status_color} />
        {:else if row.status_label}
            <span>{row.status_label}</span>
        {:else}
            <span>{row.status}</span>
        {/if}
    </Table.Cell>
{/snippet}

<div>
    <Shgrid
        fetcher={async ({ limit, offset, filters, sorter }) => {
            if (view === 'relatedList' && relatedData) {
                let relatedColumn = relatedData.column ;
                let record_id = relatedData.record_id;
                filters = { ...filters, [relatedColumn] : record_id };
            }
        const res = await axios.get(route('web-api.Job.index', { limit, offset, filters, sorter }));
        return { data: res.data, error: null };
    }}
        limit={100}
        bind:columns={cols}
        {...(view === 'related' ? { onRowClick } : {})}
        bind:selected={selectedJobs}
        {...(view === 'multi_related' ? { onSubmitClick } : {})}
        entityColumn="title"
        {title}
        {headerSnippet}
    ></Shgrid>
</div>
